import { locationApi } from '../api/axios';
import type { SyntheseSpotters } from './missions';

/* Collectes d'une mission, telles que location-service les renvoie au client
   (ProjectionSoumission.pourLeClient) : réponses, position, statut, photo,
   dates. Jamais d'identité de Spotter. */

export interface Collecte {
  id: string;
  questId?: string;
  reponsesJson?: unknown;
  latitude?: number | string | null;
  longitude?: number | string | null;
  statut?: string;
  photoUrl?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface StatsCollectes {
  total?: number;
  valides?: number;
  rejetees?: number;
  enAttente?: number;
  expirees?: number;
  spotters?: SyntheseSpotters | null;
}

export const LIBELLE_STATUT_COLLECTE: Record<string, string> = {
  valide: 'Validée',
  en_attente: 'En attente',
  rejete: 'Rejetée',
  expire: 'Expirée',
};

export async function chargerCollectes(questId: string): Promise<Collecte[]> {
  const res = await locationApi.get(`/quest/${questId}/submissions`);
  return Array.isArray(res.data) ? res.data : [];
}

export async function chargerStats(questId: string): Promise<StatsCollectes> {
  const res = await locationApi.get(`/quest/${questId}/submissions/stats`);
  return res.data ?? {};
}

/* ── Réponses ─────────────────────────────────────────────────────────────
   Format réel (app Spotter, ValidationChamps côté serveur) : une liste
   [{ label, valeur }] sérialisée en JSON. Un objet { libellé: valeur } est
   encore accepté (anciennes données). Tout le reste donne une liste vide. */

export interface Reponse { label: string; valeur: unknown }

export const normaliserLibelle = (l: string) => l.trim().toLowerCase();

export function lireReponses(brut: unknown): Reponse[] {
  let valeur = brut;
  if (typeof valeur === 'string') {
    try { valeur = JSON.parse(valeur); } catch { return []; }
  }
  if (Array.isArray(valeur)) {
    return valeur
      .filter((r): r is Record<string, unknown> => !!r && typeof r === 'object' && r.label != null)
      .map(r => ({ label: String(r.label), valeur: r.valeur }));
  }
  if (valeur && typeof valeur === 'object') {
    return Object.entries(valeur as Record<string, unknown>).map(([label, v]) => ({ label, valeur: v }));
  }
  return [];
}

/** Réponses indexées par libellé normalisé (jointure avec les champs de la mission). */
export function reponsesParLibelle(brut: unknown): Map<string, unknown> {
  return new Map(lireReponses(brut).map(r => [normaliserLibelle(r.label), r.valeur]));
}

/** Valeur lisible : texte tel quel, oui/non, nombres en français, position, listes. */
export function texteReponse(v: unknown): string {
  if (v == null || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Oui' : 'Non';
  if (typeof v === 'string') return v;
  if (typeof v === 'number') return v.toLocaleString('fr-FR');
  if (Array.isArray(v)) return v.length === 0 ? '—' : v.map(texteReponse).join(', ');
  if (typeof v === 'object') {
    const o = v as Record<string, unknown>;
    if (typeof o.lat === 'number' && typeof o.lng === 'number') {
      return `${o.lat.toFixed(5)}, ${o.lng.toFixed(5)}`;
    }
  }
  try { return JSON.stringify(v); } catch { return String(v); }
}

/* ── Photos ───────────────────────────────────────────────────────────────
   L'app envoie chaque photo à POST /blobs/photos (conteneur « images ») et
   garde le nom du fichier : dans photoUrl et dans les réponses des champs de
   type photo. Le portail les lit par GET /blobs/images/{nom}, avec le jeton. */

/** Nom de fichier d'une photo, à partir d'un nom ou d'une ancienne URL complète. Null si invalide. */
export function nomPhoto(valeur: unknown): string | null {
  if (typeof valeur !== 'string' || !valeur.trim()) return null;
  let nom = valeur.trim();
  if (/^https?:\/\//i.test(nom)) {
    try { nom = decodeURIComponent(new URL(nom).pathname.split('/').pop() || ''); } catch { return null; }
  }
  // Un nom de fichier, jamais un chemin.
  return /^[^/\\]+\.[A-Za-z0-9]{2,5}$/.test(nom) ? nom : null;
}

/** Toutes les photos d'une collecte : photoUrl et réponses de type liste de fichiers, sans doublon. */
export function photosDeLaCollecte(c: Collecte): string[] {
  const noms = new Set<string>();
  const ajouter = (v: unknown) => { const n = nomPhoto(v); if (n) noms.add(n); };
  ajouter(c.photoUrl);
  for (const r of lireReponses(c.reponsesJson)) {
    if (Array.isArray(r.valeur)) r.valeur.forEach(ajouter);
  }
  return Array.from(noms);
}

export async function chargerPhoto(nom: string): Promise<Blob> {
  const res = await locationApi.get(`/blobs/images/${encodeURIComponent(nom)}`, { responseType: 'blob' });
  return res.data instanceof Blob ? res.data : new Blob([res.data]);
}

/* ── Export serveur ───────────────────────────────────────────────────────
   GET /api/export/{questId}?format=excel : toutes les réponses, séparateur
   point-virgule, BOM UTF-8 (docs/export-reponses-client.md, location-service). */
export async function telechargerExport(questId: string): Promise<{ blob: Blob; nom: string }> {
  const res = await locationApi.get(`/api/export/${questId}`, { params: { format: 'excel' }, responseType: 'blob' });
  const entete = String(res.headers?.['content-disposition'] ?? '');
  const nom = /filename="([^"]+)"/.exec(entete)?.[1] ?? `export-${questId}.csv`;
  const blob = res.data instanceof Blob ? res.data : new Blob([res.data], { type: 'text/csv;charset=utf-8' });
  return { blob, nom };
}
